// Schema for code-owned experiment design.
//
// Code owns: structural design (variants, weights, targeting, mutual exclusion).
// UI owns: runtime state (enabled, rolloutPercentage, startDate, decision, …).
// The sync script PATCHes only code-owned fields; UI-owned fields are seeded
// with safe defaults on first create and never touched again from code.
//
// See docs/ab-testing.md for the full ownership split.

export type EvaluationMode = 'local' | 'remote';
export type BucketingUnit = 'User' | 'Group';
export type ExperimentType = 'a-b-test' | 'multi-armed-bandit' | 'release';

export interface VariantConfig {
  readonly key: string;
  readonly description?: string;
  readonly payload?: Readonly<Record<string, unknown>>;
}

export interface TargetSegmentCondition {
  /**
   * Property name to test against. Despite the JSON field being `prop` (not
   * `property`), this is the same identifier you'd see in Amplitude as a user
   * property — e.g. `funnel_slug`, `country`, `platform`.
   */
  readonly prop: string;
  /**
   * Source of the property. `'property'` covers user properties + cohorts +
   * groups — empirically the only value Amplitude's experiments API accepts.
   */
  readonly type: 'property';
  readonly op:
    | 'is'
    | 'is not'
    | 'contains'
    | 'does not contain'
    | 'set is'
    | 'set is not'
    | 'less'
    | 'less or equal'
    | 'greater'
    | 'greater or equal'
    | 'regex match'
    | 'regex does not match';
  readonly values: ReadonlyArray<string>;
}

export interface TargetSegment {
  readonly name: string;
  readonly conditions: ReadonlyArray<TargetSegmentCondition>;
  /**
   * Percentage of segment-matched users entering the experiment (0–100).
   * Amplitude's API rejects targetSegments without this field — make it
   * explicit even when it's 100.
   */
  readonly percentage: number;
  /**
   * Per-segment variant weights. Required by Amplitude even when they match
   * the experiment-level rolloutWeights — segments can in principle have a
   * different distribution than the overall flag.
   */
  readonly rolloutWeights: Readonly<Record<string, number>>;
  readonly variant?: string;
}

export interface ExperimentDefinitionInput<TVariantKey extends string = string> {
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly evaluationMode: EvaluationMode;
  /**
   * Synced to Amplitude only for remote-eval experiments. Amplitude's
   * local-evaluation engine rejects an explicit bucketingKey and uses
   * `amplitude_id` regardless — keep it in the schema as design intent but
   * understand sync is a no-op for local-eval flags.
   */
  readonly bucketingKey: string;
  /**
   * Synced to Amplitude only for remote-eval experiments. Same constraint as
   * bucketingKey above.
   */
  readonly bucketingUnit: BucketingUnit;
  readonly variants: ReadonlyArray<VariantConfig & { readonly key: TVariantKey }>;
  readonly rolloutWeights: Readonly<Record<TVariantKey, number>>;
  /**
   * Key of the mutual exclusion group this experiment belongs to. The sync
   * script cross-validates this against the groups in experiments/_groups.ts
   * (typos fail at sync time). Slot membership inside the group is NOT
   * automated — set it manually in the Amplitude UI after the group exists.
   */
  readonly mutualExclusionGroup?: string;
  readonly targetSegments?: ReadonlyArray<TargetSegment>;
  /**
   * Rejected by Amplitude on local-eval experiments. The validator below
   * blocks combining stickyBucketing:true with evaluationMode:'local'.
   */
  readonly stickyBucketing?: boolean;
  readonly experimentType?: ExperimentType;
  readonly tags?: ReadonlyArray<string>;
  /**
   * Code-only metadata. Amplitude's Management API has no equivalent field,
   * so this is for humans (PR review, dashboards, future tooling). The sync
   * script does NOT send it to Amplitude.
   */
  readonly primaryMetric?: string;
}

export interface ExperimentDefinition<TVariantKey extends string = string>
  extends ExperimentDefinitionInput<TVariantKey> {
  readonly __experiment: true;
}

export interface MutualExclusionGroupInput {
  readonly key: string;
  readonly name: string;
  readonly description?: string;
}

export interface MutualExclusionGroup extends MutualExclusionGroupInput {
  readonly __mutualExclusionGroup: true;
}

/**
 * Define an experiment. Pass a literal object so TypeScript can infer variant
 * keys and surface them downstream in `<Variant value="…">` autocomplete.
 *
 * Example:
 *   export default defineExperiment({
 *     key: 'welcome_screen_v1',
 *     name: 'Welcome screen v1',
 *     description: '...',
 *     evaluationMode: 'local',
 *     bucketingKey: 'amplitude_id',
 *     bucketingUnit: 'User',
 *     variants: [{ key: 'control' }, { key: 'treatment_a' }],
 *     rolloutWeights: { control: 1, treatment_a: 1 },
 *     mutualExclusionGroup: 'welcome',
 *   });
 */
export function defineExperiment<const T extends ExperimentDefinitionInput>(
  config: T,
): T & { readonly __experiment: true } {
  validateExperimentShape(config);
  return { ...config, __experiment: true } as T & { readonly __experiment: true };
}

export function defineMutualExclusionGroup<const T extends MutualExclusionGroupInput>(
  config: T,
): T & { readonly __mutualExclusionGroup: true } {
  if (!config.key || !/^[a-z0-9_-]+$/i.test(config.key)) {
    throw new Error(
      `[experiments] defineMutualExclusionGroup: invalid key "${String(config.key)}" — must match /^[a-z0-9_-]+$/i`,
    );
  }
  if (!config.name) {
    throw new Error(`[experiments] defineMutualExclusionGroup("${config.key}"): name is required`);
  }
  return { ...config, __mutualExclusionGroup: true } as T & { readonly __mutualExclusionGroup: true };
}

function validateExperimentShape(config: ExperimentDefinitionInput): void {
  const where = `[experiments] defineExperiment("${String(config.key)}")`;

  if (!config.key || !/^[a-z0-9_-]+$/i.test(config.key)) {
    throw new Error(`${where}: invalid key — must match /^[a-z0-9_-]+$/i`);
  }
  if (!config.name) throw new Error(`${where}: name is required`);
  if (!config.description) throw new Error(`${where}: description is required`);
  if (config.evaluationMode !== 'local' && config.evaluationMode !== 'remote') {
    throw new Error(`${where}: evaluationMode must be 'local' or 'remote'`);
  }
  if (!Array.isArray(config.variants) || config.variants.length < 2) {
    throw new Error(`${where}: at least 2 variants required`);
  }

  const variantKeys = new Set<string>();
  for (const v of config.variants) {
    if (!v.key) throw new Error(`${where}: variant.key is required`);
    if (variantKeys.has(v.key)) {
      throw new Error(`${where}: duplicate variant key "${v.key}"`);
    }
    variantKeys.add(v.key);
  }

  // Every variant must have a weight; every weight must reference a real variant.
  const weightKeys = Object.keys(config.rolloutWeights);
  for (const wk of weightKeys) {
    if (!variantKeys.has(wk)) {
      throw new Error(`${where}: rolloutWeights references unknown variant "${wk}"`);
    }
  }
  for (const vk of variantKeys) {
    if (!(vk in config.rolloutWeights)) {
      throw new Error(`${where}: rolloutWeights missing weight for variant "${vk}"`);
    }
  }
  for (const w of Object.values(config.rolloutWeights)) {
    if (typeof w !== 'number' || !Number.isFinite(w) || w < 0) {
      throw new Error(`${where}: rolloutWeights values must be non-negative numbers`);
    }
  }

  // Targeting / segments: each segment's variant (if pinned) must exist.
  if (config.targetSegments) {
    for (const seg of config.targetSegments) {
      if (seg.variant && !variantKeys.has(seg.variant)) {
        throw new Error(
          `${where}: targetSegment "${seg.name}" pins unknown variant "${seg.variant}"`,
        );
      }
    }
  }

  // Local-eval constraint enforced by the Amplitude API.
  if (config.evaluationMode === 'local' && config.stickyBucketing === true) {
    throw new Error(
      `${where}: stickyBucketing is not supported when evaluationMode is 'local'. Remove the stickyBucketing field or switch to evaluationMode: 'remote'.`,
    );
  }
}

/** Type guard used by the registry + sync script. */
export function isExperimentDefinition(value: unknown): value is ExperimentDefinition {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { __experiment?: unknown }).__experiment === true
  );
}

/** Type guard used by the registry + sync script. */
export function isMutualExclusionGroup(value: unknown): value is MutualExclusionGroup {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { __mutualExclusionGroup?: unknown }).__mutualExclusionGroup === true
  );
}
